import clsx from 'clsx';
import { File as FileIcon, UploadCloud } from 'lucide-react';
import React, { useRef, useState } from 'react';

import { TemplateSectionCard } from '@/components/templates-v2/document-generation/templates/components';
import { Button } from '@/components/ui/button';

interface FirmHipaaStaticFileManagerProps {
  title: string;
  description: string;
  downloadUrl: string | null;
  handleUpdate: (file: File | null) => void;
  handleClear: () => void;
  isLoading?: boolean;
  /** Show an inline preview of the PDF when available */
  enablePreview?: boolean;
  /** Accepted file MIME types/extensions (input accept attribute). */
  accept?: string;
}

export const FirmHipaaStaticFileManagerContent = ({
  downloadUrl,
  handleUpdate,
  handleClear,
  isLoading = false,
  enablePreview = false,
  accept = 'application/pdf',
}: Omit<FirmHipaaStaticFileManagerProps, 'title' | 'description'>) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLoading) return;
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLoading) return;
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpdate(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (isLoading) return;
    if (e.target.files && e.target.files[0]) {
      handleUpdate(e.target.files[0]);
    }
  };

  const onButtonClick = () => {
    if (isLoading) return;
    inputRef.current?.click();
  };

  return (
    <div
      className="w-full"
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onSubmit={(e) => e.preventDefault()}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleChange}
        disabled={isLoading}
      />

      {downloadUrl ? (
        <div>
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <FileIcon className="h-6 w-6 text-gray-500" />
              <span className="font-medium">Manage existing file</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => window.open(downloadUrl, '_blank')}
                disabled={isLoading}
              >
                Download
              </Button>
              <Button
                variant="outline"
                onClick={onButtonClick}
                disabled={isLoading}
              >
                {isLoading ? 'Replacing...' : 'Replace'}
              </Button>
              <Button
                variant="destructive"
                className="text-white"
                onClick={handleClear}
                disabled={isLoading}
              >
                {isLoading ? 'Clearing...' : 'Clear'}
              </Button>
            </div>
          </div>
          {enablePreview && downloadUrl && (
            <div className="mt-4 border rounded-lg overflow-hidden">
              <iframe
                title={`${downloadUrl} preview`}
                src={downloadUrl}
                className="w-full h-96"
              />
            </div>
          )}
        </div>
      ) : (
        <div
          role="presentation"
          onClick={onButtonClick}
          className={clsx(
            'relative flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors',
            {
              'border-primary bg-primary-foreground/10':
                dragActive && !isLoading,
              'border-border bg-background hover:bg-muted/50':
                !dragActive && !isLoading,
              'cursor-not-allowed bg-muted/50': isLoading,
            },
          )}
        >
          <div className="flex flex-col items-center justify-center pb-6 pt-5">
            <UploadCloud
              className={clsx('mb-4 h-8 w-8', {
                'text-muted-foreground': !dragActive,
                'text-primary': dragActive,
              })}
            />
            <p className="mb-2 text-sm text-muted-foreground">
              <span className="font-semibold">Click to upload</span> or drag and
              drop
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export const FirmHipaaStaticFileManager: React.FC<
  FirmHipaaStaticFileManagerProps
> = ({
  title,
  description,
  downloadUrl,
  handleUpdate,
  handleClear,
  isLoading = false,
  enablePreview = false,
  accept = 'application/pdf',
}) => {
  return (
    <TemplateSectionCard title={title} description={description}>
      <FirmHipaaStaticFileManagerContent
        downloadUrl={downloadUrl}
        handleUpdate={handleUpdate}
        handleClear={handleClear}
        isLoading={isLoading}
        enablePreview={enablePreview}
        accept={accept}
      />
    </TemplateSectionCard>
  );
};
